import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { format, subDays, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardBody,
  Select,
  Spinner,
} from '@/components/ui';
import { paymentLinkApi, transactionApi, getErrorMessage } from '@/lib/api';
import type { PaymentLinkStatistics, TransactionStatistics, PaymentMethod } from '@/types';

const dateRangeOptions = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

function getDateRange(range: string): { startDate?: Date; endDate?: Date } {
  const now = new Date();
  switch (range) {
    case 'today':
      return {
        startDate: new Date(now.setHours(0, 0, 0, 0)),
        endDate: new Date(),
      };
    case '7days':
      return {
        startDate: subDays(now, 7),
        endDate: new Date(),
      };
    case '30days':
      return {
        startDate: subDays(now, 30),
        endDate: new Date(),
      };
    case 'thisMonth':
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now),
      };
    case 'lastMonth':
      const lastMonth = subDays(startOfMonth(now), 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      };
    case 'thisYear':
      return {
        startDate: startOfYear(now),
        endDate: new Date(),
      };
    default:
      return {};
  }
}

export function ReportsPage() {
  const [dateRange, setDateRange] = useState('30days');
  const { startDate, endDate } = getDateRange(dateRange);

  const { data: linkStats, isLoading: linkStatsLoading } = useQuery({
    queryKey: ['payment-link-statistics', dateRange],
    queryFn: async () => {
      const response = await paymentLinkApi.getStatistics({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      return response.data.data as PaymentLinkStatistics;
    },
  });

  const { data: txStats, isLoading: txStatsLoading } = useQuery({
    queryKey: ['transaction-statistics', dateRange],
    queryFn: async () => {
      const response = await transactionApi.getStatistics({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      return response.data.data as TransactionStatistics;
    },
  });

  const handleExportTransactions = async () => {
    try {
      const response = await transactionApi.exportCsv({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Transactions exported successfully');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(amount);
  };

  const getPaymentMethodLabel = (method: PaymentMethod | null) => {
    if (!method) return 'Other';
    const labels: Record<PaymentMethod, string> = {
      CARD: 'Card',
      GCASH: 'GCash',
      MAYA: 'Maya',
      GRAB_PAY: 'GrabPay',
      BANK_TRANSFER: 'Bank Transfer',
      OTHER: 'Other',
    };
    return labels[method] || 'Other';
  };

  const isLoading = linkStatsLoading || txStatsLoading;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Analytics and insights for your payment operations</p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            options={dateRangeOptions}
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="w-40"
          />
          <Button
            variant="outline"
            leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
            onClick={handleExportTransactions}
          >
            Export
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* Overview stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardBody className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary-50 rounded-lg">
                    <BanknotesIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(txStats?.overview?.totalAmount || 0)}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-success-50 rounded-lg">
                    <CheckCircleIcon className="w-6 h-6 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Successful Payments</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {txStats?.overview?.successfulTransactions || 0}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-info-50 rounded-lg">
                    <ChartBarIcon className="w-6 h-6 text-info" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Success Rate</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {(txStats?.overview?.successRate || 0).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-warning-50 rounded-lg">
                    <CalendarDaysIcon className="w-6 h-6 text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg. Per Transaction</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(
                        txStats?.overview?.successfulTransactions
                          ? (txStats.overview.totalAmount || 0) / txStats.overview.successfulTransactions
                          : 0
                      )}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Payment Links stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Links Overview</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-primary rounded-full"></div>
                      <span className="text-gray-600">Total Links Created</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {linkStats?.totalLinks || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-success rounded-full"></div>
                      <span className="text-gray-600">Paid Links</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {linkStats?.paidLinks || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-info rounded-full"></div>
                      <span className="text-gray-600">Active Links</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {linkStats?.activeLinks || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-warning rounded-full"></div>
                      <span className="text-gray-600">Expired Links</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {linkStats?.expiredLinks || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <span className="text-gray-600">Cancelled Links</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {linkStats?.cancelledLinks || 0}
                    </span>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">Conversion Rate</span>
                    <span className="text-2xl font-bold text-primary">
                      {(linkStats?.conversionRate || 0).toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Percentage of payment links that resulted in successful payments
                  </p>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Transaction Status</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-success" />
                      <span className="text-gray-600">Successful</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900">
                        {txStats?.overview?.successfulTransactions || 0}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">
                        ({formatCurrency(txStats?.overview?.totalAmount || 0)})
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <ClockIcon className="w-5 h-5 text-warning" />
                      <span className="text-gray-600">Pending</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {txStats?.overview?.pendingTransactions || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <XCircleIcon className="w-5 h-5 text-error" />
                      <span className="text-gray-600">Failed</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {txStats?.overview?.failedTransactions || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <CreditCardIcon className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600">Total Refunded</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(txStats?.overview?.totalRefunded || 0)}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Payment Method Breakdown */}
          <Card>
            <CardBody>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h2>
              {txStats?.paymentMethodBreakdown && txStats.paymentMethodBreakdown.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Payment Method</th>
                        <th className="text-right">Transactions</th>
                        <th className="text-right">Amount</th>
                        <th className="text-right">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {txStats.paymentMethodBreakdown.map((pm, index) => {
                        const totalAmount = txStats.overview?.totalAmount || 1;
                        const share = ((pm.amount / totalAmount) * 100).toFixed(1);
                        return (
                          <tr key={index}>
                            <td>
                              <div className="flex items-center gap-2">
                                <CreditCardIcon className="w-5 h-5 text-gray-400" />
                                <span className="font-medium">
                                  {getPaymentMethodLabel(pm.method)}
                                </span>
                              </div>
                            </td>
                            <td className="text-right">{pm.count}</td>
                            <td className="text-right font-medium">
                              {formatCurrency(pm.amount)}
                            </td>
                            <td className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-20 bg-gray-100 rounded-full h-2">
                                  <div
                                    className="bg-primary h-2 rounded-full"
                                    style={{ width: `${share}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm text-gray-500 w-12">{share}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No payment method data available for this period
                </div>
              )}
            </CardBody>
          </Card>

          {/* Daily Transactions Chart placeholder */}
          <Card>
            <CardBody>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Transactions</h2>
              {txStats?.dailyTransactions && txStats.dailyTransactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-right">Transactions</th>
                        <th className="text-right">Total Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {txStats.dailyTransactions.slice(0, 10).map((day, index) => (
                        <tr key={index}>
                          <td className="font-medium">
                            {format(new Date(day.date), 'MMM d, yyyy')}
                          </td>
                          <td className="text-right">{day.count}</td>
                          <td className="text-right font-medium">
                            {formatCurrency(day.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No daily transaction data available for this period
                </div>
              )}
            </CardBody>
          </Card>

          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gross Revenue</span>
                    <span className="font-medium">
                      {formatCurrency(txStats?.overview?.totalAmount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Refunds</span>
                    <span className="font-medium text-error">
                      -{formatCurrency(txStats?.overview?.totalRefunded || 0)}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900">Net Revenue</span>
                      <span className="font-bold text-xl text-primary">
                        {formatCurrency(
                          (txStats?.overview?.totalAmount || 0) -
                            (txStats?.overview?.totalRefunded || 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Link Performance</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Link Value</span>
                    <span className="font-medium">
                      {formatCurrency(linkStats?.totalAmount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount Collected</span>
                    <span className="font-medium text-success">
                      {formatCurrency(linkStats?.paidAmount || 0)}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900">Collection Rate</span>
                      <span className="font-bold text-xl text-primary">
                        {linkStats?.totalAmount
                          ? (((linkStats?.paidAmount || 0) / linkStats.totalAmount) * 100).toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
